// The freemium auth wall — a bottom-sheet modal raised when the gateway
// 403s a guest who has used their free generations. It does not block the
// product: signing in stays free, it just lifts the guest onto the plan
// caps and unlocks cross-device sync.
//
// Rebuilt in NIGHT_AUDIT §2.2: previously a wall of inline styles with
// non-standard font-weights (720/680/560). Now routed through the design
// system (.surface, .btn-primary, .cta-press, .chip), so dark-mode tokens
// flow through and a future palette change is one-edit.
import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { PomelaMascot } from './PomelaMascot';
import { stashComposerDraft } from '../lib/signin-draft';
import type { GenerationMode } from './PromptInput';
import { t } from '../lib/i18n';

const SWIPE_DISMISS_PX = 80;

export function AuthWall({
  onClose,
  draft
}: {
  onClose: () => void;
  /** §F02 — the just-submitted draft, stashed on sign-in so the round trip
   *  through /signin (which unmounts the composer) restores the prompt. */
  draft?: { prompt: string; mode: GenerationMode };
}): JSX.Element {
  // Drag-to-dismiss state. `drag` is the live pixel offset while a touch
  // is active; it transitions back to 0 if the gesture is released
  // before the dismiss threshold. Translating the sheet in JS (rather
  // than CSS-only) gives us the rubber-banding feel a native sheet has.
  const sheetRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState(0);
  const dragStartY = useRef<number | null>(null);
  // Pre-open focus snapshot so dismiss can restore it (WCAG 2.4.3).
  // Keyboard users who land on the sheet via Escape, scrim click, or a
  // successful sign-in continue from where they were, not from the top
  // of the page.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    try {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
    } catch {
      previousFocusRef.current = null;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      // Restore focus on unmount — best-effort, the previously-focused
      // element may have been re-rendered or removed during the modal's
      // open window.
      try {
        previousFocusRef.current?.focus?.();
      } catch {
        /* no-op */
      }
    };
  }, [onClose]);

  function onTouchStart(e: TouchEvent): void {
    if (e.touches.length !== 1) return;
    dragStartY.current = e.touches[0]?.clientY ?? null;
  }
  function onTouchMove(e: TouchEvent): void {
    if (dragStartY.current === null || e.touches.length !== 1) return;
    const dy = (e.touches[0]?.clientY ?? 0) - dragStartY.current;
    // Only respond to downward drags; pin upward drags at 0 so the sheet
    // never lifts above its resting position.
    setDrag(Math.max(0, dy));
  }
  function onTouchEnd(): void {
    if (dragStartY.current === null) return;
    if (drag > SWIPE_DISMISS_PX) {
      // Past the dismiss threshold — let the sheet animate off-screen
      // before unmounting so the gesture reads as a clean exit, not a
      // disappearance.
      setDrag(600);
      window.setTimeout(onClose, 180);
    } else {
      setDrag(0);
    }
    dragStartY.current = null;
  }

  function signIn(): void {
    navigator.vibrate?.(8);
    // §F02 — the guest hit the free-gen wall AFTER submitting, so the composer
    // was already cleared. Stash the just-submitted prompt so signing in and
    // returning restores it in the composer (signin-draft.ts) instead of
    // dropping the idea. Navigating to /signin unmounts the whole App surface.
    if (draft && draft.prompt.trim()) {
      stashComposerDraft({ prompt: draft.prompt, mode: draft.mode, assetNames: [] });
    }
    onClose();
    location.hash = 'signin';
  }

  return createPortal(
    <div
      className="authwall-scrim animate-vs-fade-in"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={sheetRef}
        className="authwall-sheet surface"
        role="dialog"
        aria-modal="true"
        aria-label={t('auth.ariaSheet')}
        onClick={(e) => e.stopPropagation()}
        style={{
          // Transient drag offset — kept inline so the gesture stays
          // 60fps without dispatching to CSS variables. The base style
          // lives in .authwall-sheet; only the live delta is here.
          transform: drag ? `translateY(${drag}px)` : undefined,
          transition: dragStartY.current === null ? 'transform 220ms var(--spring)' : 'none'
        }}
      >
        <button
          type="button"
          className="authwall-handle"
          aria-label={t('auth.ariaDismiss')}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        />
        <PomelaMascot size={82} />
        <h2 className="authwall-title">{t('auth.title')}</h2>
        <p className="authwall-sub">{t('auth.sub')}</p>
        <button
          type="button"
          className="btn btn-primary cta-press cta-shimmer authwall-cta"
          onClick={signIn}
        >
          {t('auth.cta')}
        </button>
        <ul className="authwall-trust" aria-label={t('auth.ariaGuarantees')}>
          <li className="chip authwall-chip">{t('auth.freeForever')}</li>
          <li className="chip authwall-chip">{t('auth.noCard')}</li>
          <li className="chip authwall-chip">{t('auth.magicLink')}</li>
        </ul>
        <button
          type="button"
          className="btn btn-ghost btn-press authwall-secondary"
          onClick={onClose}
        >
          {t('auth.maybeLater')}
        </button>
      </div>
    </div>,
    document.body
  );
}
